import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {defineAbilityConfig} from '../system/defineAbilityConfig';
import type {DesignerAbilityDefinition} from '../system';

import {ONLY_ON_PRIMARY_LOCALE} from './localization';
import {
  canDEAccessAssetsDefinition,
  canDEAccessCanvasDefinition,
  canDECreateComponentsDefinition,
  canDECreatePageDefinition,
  canDECreateStyleBlocksDefinition,
  canDEDesignDefinition,
  canDEEditDefinition,
  canDEInsertComponentInstanceDefinition,
  canDEManageAssetsDefinition,
  canDEManagePageSettingsDefinition,
  canDEModifyComponentsDefinition,
  canDEModifyImageElementsDefinition,
  canDEModifySelectedCampaignComponentDefinitionDefinition,
  canDEModifyStyleBlocksDefinition,
  canDEModifyInteractionsDefinition,
  canDEModifyVariablesDefinition,
  canDEReadInteractionsDefinition,
  canDEReadPageSettingsDefinition,
  canDEReadVariablesDefinition,
  canDESetInstancePropsDefinition,
  canDEWriteCodeSettingsDefinition,
} from './designerExtensions';
import {WHEN_IN_OPTIMIZE_MODE, WHEN_NOT_IN_OPTIMIZE_MODE} from './optimization';

/**
 * Used to view the right sidebar to edit an element on the Designer canvas.
 */
export const canViewDesignerRightSidebar = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    branches: {
      main: true,
    },
    siteModes: {design: true},
    workflows: {
      canvas: true,
    },
  },
  {
    branches: {
      branch: true,
    },
    siteModes: {design: true},
    workflows: {
      canvas: true,
    },
  },
  {
    abilities: {isInPageBuildMode: true},
    workflows: {
      canvas: true,
    },
  },
] as const);

export const canUndoRedo = defineAbilityConfig([
  {
    workflows: {
      canvas: true,
      dynamicData: true,
    },
    siteModes: {design: true, edit: true},
    branchMergeLocked: false,
  },
  {
    workflows: {
      canvas: true,
    },
    siteModes: {
      edit: true,
    },
    branchMergeLocked: false,
  },
  {
    workflows: {
      logicFlows: true,
      dynamicData: true,
    },
    branchMergeLocked: false,
  },
] as const);

// Allows you full edit access in the designer-canvas. Can double click
// on text to edit text, can drag and drop (assets, nodes, from the add
// panel, etc), can right click on the designer-canvas to get a context
// menu, can edit symbols, styles, ix, grid, etc.
export const canDesign = defineAbilityConfig([
  {
    featureFlags: {[FEATURE_FLAGS.REALTIME]: true},
    localization: ONLY_ON_PRIMARY_LOCALE,
    siteModes: {design: true},
    workflows: {
      canvas: true,
    },
    optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
    branchMergeLocked: false,
  },
] as const);

/**
 * Users may access the canvas workflow as long as they can either
 * access the design site mode or edit site mode.
 * Site commenters are not allowed to edit either site mode, and they
 * should not have access to the canvas workflow
 */
export const canAccessCanvasWorkflow = defineAbilityConfig([
  {abilities: {canAccessDesignSiteMode: true}},
  {abilities: {canAccessEditSiteMode: true}},
  {abilities: {canAccessViewSiteMode: true}},
] as const);

export const canAccessEmailSettings = defineAbilityConfig([
  {
    siteModes: {design: true},
    permission: ['manage', 'siteCommerce'],
    features: {ecommerce: true},
  },
  {
    siteModes: {edit: true},
    permission: ['manage', 'siteCommerce'],
    features: {ecommerce: true},
  },
  {
    siteModes: {design: true},
    features: {siteUsers: true},
  },
] as const);

/**
 * This allows access to the share button in the TopBar on the right
 * which opens a small menu and lets user:
 * - invite collaborators
 * - create/regenerate a share link and copy it to the clipboard
 */
export const canAccessShareButton = defineAbilityConfig([
  {
    workflows: {
      canvas: true,
      comment: true,
      preview: true,
      logicFlows: true,
      dynamicData: true,
      optimization: true,
      analyzeVisualView: true,
      optimizationClickGoal: true,
      optimizationRedirectTest: true,
    },
  },
] as const);

/**
 * Allows access to create/regenerate a share link and copy it to the clipboard
 */
export const canShare = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    branches: {
      main: true,
    },
    permission: ['share', 'site'],
  },
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    siteModes: {edit: true},
    permission: ['share', 'site'],
  },
  {
    workflows: {comment: true},
    permission: ['share', 'site'],
  },
  {
    branches: {
      branch: true,
    },
    permission: ['share', 'site'],
  },
] as const);

// This is the ability to create a css class
export const canCreateStyleBlocks = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    siteModes: {design: true},
    workflows: {canvas: true},
    permission: ['create', 'siteStyleBlock'],
    localization: ONLY_ON_PRIMARY_LOCALE,
    optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
    branchMergeLocked: false,
  },
  {
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    siteModes: {design: true},
    workflows: {canvas: true},
    permission: ['create', 'siteStyleBlock'],
    localization: ONLY_ON_PRIMARY_LOCALE,
    optimization: WHEN_IN_OPTIMIZE_MODE,
    selectedElementOptimized: true,
    branchMergeLocked: false,
  },
] as const);

export const canModifyPseudoStyleRuleToken = defineAbilityConfig({
  abilities: {canCreateStyleBlocks: true},
  optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
} as const);

// This is the ability to modify a css class that
// might have unintended consequences
export const canModifyStyleBlocks = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: true,
  },
  siteModes: {design: true},
  workflows: {canvas: true},
  permission: ['modify', 'siteStyleBlock'],
  branchMergeLocked: false,
} as const);

// This is the ability to modify a css class that was created
// by the current user
export const canModifyOwnStyleBlocks = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: true,
  },
  siteModes: {design: true},
  workflows: {canvas: true},
  permission: ['modifyOwn', 'siteStyleBlock'],
  localization: ONLY_ON_PRIMARY_LOCALE,
  branchMergeLocked: false,
} as const);

// This is the ability to create a symbol
export const canCreateSymbols = defineAbilityConfig([
  {
    abilities: {canEditLocaleContent: true},
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
    siteModes: {design: true},
    workflows: {canvas: true},
    permission: ['create', 'siteSymbol'],
    branchMergeLocked: false,
  },
] as const);

// This is the ability to modify a Horizontal Settings (settings available for all Elements) of an Element while inside a Component
export const canModifyHorizontalSettingsInsideComponent = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    workflows: {canvas: true},
    permission: ['modify', 'siteSymbol'],
    siteModes: {design: true},
    localization: ONLY_ON_PRIMARY_LOCALE,
    optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
  },
] as const);

// This is the ability to modify the data type of a component property (such as changing it from Text to Image)
export const canModifyComponentPropertyDataType = defineAbilityConfig({
  localization: ONLY_ON_PRIMARY_LOCALE,
  optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
} as const);

// Allows viewing the asset manager. When this is false the asset manager is hidden.
export const canAccessAssets = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: true,
  },
  siteModes: {design: true, edit: true},
  workflows: {canvas: true, preview: true},
  isEditingCodeComponent: false,
} as const);

// Allows accessing the asset manager and managing assets and asset folders.
export const canManageAssets = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: true,
  },
  workflows: {canvas: true},
  permission: ['manage', 'siteFile'],
} as const);

// This is specific to cross-site copy. We want to enable cross-site
// copy when you preview a backup or when you're looking at something
// like a showcase site or template that you're allowed to "clone".
// but if you're previewing a share link for someone else's site or
// something you're not allowed to clone then we don't allow
// cross-site copying things from the site.
export const canCrossSiteCopy = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    siteModes: {design: true},
    workflows: {
      canvas: true,
    },
  },
  {
    userTypes: {
      unauthorized: true,
    },
    workflows: {
      canvas: true,
    },
    features: {cloneable: true},
  },
] as const);

export const canDesignOrCrossSiteCopy = defineAbilityConfig([
  {abilities: {canDesign: true}},
  {abilities: {canCrossSiteCopy: true}},
] as const);

export const canAccessCanvasContextMenu = defineAbilityConfig([
  {
    abilities: {canDesignOrCrossSiteCopy: true},
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    branches: {
      main: true,
    },
    workflows: {
      canvas: true,
    },
    branchMergeLocked: false,
  },
  {
    abilities: {canDesignOrCrossSiteCopy: true},
    branches: {
      branch: true,
    },
    workflows: {
      canvas: true,
    },
    branchMergeLocked: false,
  },
] as const);

export const canAccessUserSystems = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: true,
  },
  branches: {
    main: true,
  },
  siteModes: {design: true, edit: true},
  workflows: {canvas: true, dynamicData: true, agents: true},
  optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
  features: {siteUsers: true},
} as const);

export const canAccessBackups = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
  },
  siteModes: {design: true},
  permissions: [
    ['manage', 'siteSnapshot'],
    ['design', 'site'],
  ],
} as const);

export const navigatorStandardMode = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: true,
  },
  branches: {
    main: true,
  },
  siteModes: {design: true},
  workflows: {
    canvas: true,
  },
} as const);

export const navigatorInBranchMode = defineAbilityConfig({
  branches: {
    branch: true,
  },
  siteModes: {design: true},
  workflows: {
    canvas: true,
  },
} as const);

export const navigatorCampaignBuildMode = defineAbilityConfig({
  // Campaign Build Mode - allow navigator in edit mode (feature-flagged)
  abilities: {canAccessNavigatorInCampaigns: true},
  workflows: {
    canvas: true,
  },
} as const);

export const navigatorPageBuildMode = defineAbilityConfig({
  workflows: {
    canvas: true,
  },
  abilities: {
    isInPageBuildMode: true,
    isInCampaignBuildMode: false,
  },
} as const);

export const canAccessNavigator = defineAbilityConfig([
  {abilities: {navigatorStandardMode: true}},
  {abilities: {navigatorInBranchMode: true}},
  {abilities: {navigatorCampaignBuildMode: true}},
  {abilities: {navigatorPageBuildMode: true}},
] as const);

export const canAccessProjectSettings = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: true,
  },
  workflows: {canvas: true},
  siteModes: {design: true, edit: true},
} as const);

export const canAccessGeneralSettings = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: true,
  },
  workflows: {canvas: true},
  siteModes: {design: true},
  features: {onboarding: true},
} as const);

export const canAccessInterfaceSettings = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: true,
  },
  workflows: {canvas: true},
  features: {onboarding: true},
} as const);

export const canAccessLocalizationSettings = defineAbilityConfig({
  workflows: {canvas: true},
  siteModes: {design: true},
  siteKinds: {
    webflow: true,
    tutorial: true,
    cloudapp: true,
    optimize: true,
    campaign: false,
  },
  permission: ['design', 'site'],
} as const);

export const canAccessIntellimizeIntegration = defineAbilityConfig({
  featureFlags: {
    [FEATURE_FLAGS.INTELLIMIZE_INTEGRATION]: true,
  },
} as const);

export const canAccessAddPanel = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    branches: {
      main: true,
    },
    siteModes: {design: true},
    workflows: {
      canvas: true,
      dynamicData: true,
    },
    isEditingCodeComponent: false,
  },
  {
    branches: {
      branch: true,
    },
    siteModes: {design: true},
    workflows: {
      canvas: true,
      dynamicData: true,
    },
    optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
    isEditingCodeComponent: false,
  },
  {
    // Campaign Build Mode - allow add panel in edit mode
    abilities: {isInCampaignBuildMode: true},
    workflows: {
      canvas: true,
    },
    isEditingCodeComponent: false,
  },
] as const);

export const canUseAddPanelShortcut = defineAbilityConfig({
  localization: ONLY_ON_PRIMARY_LOCALE,
  optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
  isEditingCodeComponent: false,
} as const);

export const canAccessCopilot = defineAbilityConfig([
  {
    featureFlags: {[FEATURE_FLAGS.COPILOT]: true},
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    workflows: {
      canvas: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
  },
  {
    featureFlags: {[FEATURE_FLAGS.COPILOT]: true},
    siteModes: {design: true},
    workflows: {
      canvas: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
  },
] as const);

/**
 * Determines whether legacy AIDA/Copilot entry points should be shown.
 * Returns false when the new AI Assistant feature flag is enabled,
 * effectively hiding all legacy AIDA entry points (including for AI Site Gen sites).
 */
export const canShowLegacyAida = defineAbilityConfig({
  featureFlags: {[FEATURE_FLAGS.AI_ASSISTANT]: false},
} as const);

export const canAccessStylesManagerPanel = defineAbilityConfig({
  abilities: {canDesign: true},
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: true,
  },
  siteModes: {design: true},
  workflows: {
    canvas: true,
  },
} as const);

export const canAccessCustomCssPropertiesValues = defineAbilityConfig([
  {
    abilities: {canDesign: true},
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    siteModes: {design: true},
    workflows: {
      canvas: true,
    },
  },
  {
    abilities: {canDesign: true},
    branches: {
      branch: true,
    },
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    siteModes: {design: true},
    workflows: {
      canvas: true,
    },
  },
  {
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    siteModes: {design: true},
    workflows: {canvas: true},
    optimization: WHEN_IN_OPTIMIZE_MODE,
  },
] as const);

export const canEditCanvas = defineAbilityConfig([
  {
    siteModes: {design: true, edit: true},
    workflows: {canvas: true},
    branchMergeLocked: false,
  },
  {
    // readonly mode
    userTypes: {
      unauthorized: true,
    },
    siteModes: {design: true, edit: true},
    branchMergeLocked: false,
  },
  {
    branches: {
      branch: true,
    },
    branchMergeLocked: false,
  },
  {
    abilities: {isInPageBuildMode: true},
    siteModes: {design: true, edit: true},
    branchMergeLocked: false,
  },
  {
    abilities: {isInCampaignBuildMode: true},
    siteModes: {design: true, edit: true},
    branchMergeLocked: false,
  },
] as const);

export const canAccessComponentsPanel = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    branches: {
      main: true,
    },
    siteModes: {design: true},
    workflows: {
      canvas: true,
    },
  },
  {
    branches: {
      branch: true,
    },
    siteModes: {design: true},
    workflows: {
      canvas: true,
    },
    optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
  },
  {
    workflows: {canvas: true},
    abilities: {isInPageBuildMode: true},
  },
] as const);

export const canUseComponentsPanelShortcut = defineAbilityConfig([
  {localization: ONLY_ON_PRIMARY_LOCALE, siteModes: {design: true}},
  {
    localization: ONLY_ON_PRIMARY_LOCALE,
    abilities: {isInPageBuildMode: true},
  },
] as const);

export const canAccessHelpPanels = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: true,
  },
  workflows: {canvas: true, comment: true},
} as const);

export const canAccessAuditorPanel = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    branches: {
      main: true,
    },
    siteModes: {design: true},
    workflows: {
      canvas: true,
    },
  },
  {
    branches: {
      branch: true,
    },
    siteModes: {design: true},
    workflows: {
      canvas: true,
    },
  },
] as const);

export const canAddNewBreakpoint = defineAbilityConfig({
  siteModes: {design: true},
  workflows: {canvas: true, preview: true},
  optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
} as const);

export const canAccessAllNodes = defineAbilityConfig([
  {siteModes: {design: true}},
  {workflows: {comment: true}, siteModes: {design: true, edit: true}},
] as const);

export const canAccessMiniToolbar = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
  },
  workflows: {canvas: true},
  siteModes: {edit: true},
} as const);

export const canAccessOutlineBreadcrumbs = defineAbilityConfig({
  siteModes: {design: true},
} as const);

export const canSaveAsSnapshot = defineAbilityConfig({
  siteModes: {design: true},
  permissions: [['manage', 'siteSnapshot']],
} as const);

/**
 * Can the user reset properties to default values
 *
 * Also enabled in Build mode, where marketers edit property values and
 * so need the overridden indicator and the reset it carries.
 */
export const canResetPropertiesToDefault = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    siteModes: {design: true},
    localization: ONLY_ON_PRIMARY_LOCALE,
  },
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    abilities: {isInPageBuildMode: true},
    localization: ONLY_ON_PRIMARY_LOCALE,
  },
] as const);

/**
 * Can the user modify the default value of a component prop?
 */
export const canModifyPropertyDefaultValue = defineAbilityConfig({
  optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
} as const);

/**
 * Defines can user see grid structure on the Canvas
 * when they select immediate child of the element with display grid
 */
export const canViewGridStructure = defineAbilityConfig({
  siteModes: {design: true},
} as const);

export const canAccessLayoutOnCanvasControls = defineAbilityConfig({
  siteModes: {design: true},
  optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
} as const);

export const canViewCssPreview = defineAbilityConfig({
  siteModes: {design: true},
} as const);

export const canAccessCodeLibraries = defineAbilityConfig({
  siteModes: {
    design: true,
  },
  userTypes: {
    authorizedInDesigner: true,
  },
} as const);

export const canAccessGsapIntegrationSettings = defineAbilityConfig([
  // Full access for site managers and designers
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    workflows: {canvas: true},
    siteModes: {design: true},
  },
] as const);

/**
 * Designer Extension ability to know if the user can programmatically access the canvas and
 * its underlying pieces (elements, styles, etc).
 * This should only be used for Designer Extension APIs.
 */
export const canDEAccessCanvas: DesignerAbilityDefinition =
  canDEAccessCanvasDefinition;

/**
 * Designer Extension ability to know if the user can update elements or attributes of elements on the canvas.
 * This should only be used for Designer Extension APIs.
 */
export const canDEDesign: DesignerAbilityDefinition = canDEDesignDefinition;

/**
 * Designer Extension ability for inserting component instances onto the canvas.
 * Broader than canDEDesign — permits Campaign Build mode. Intended to be used
 * alongside canDEDesign via payload-dependent selection on element-insert APIs.
 * This should only be used for Designer Extension APIs.
 */
export const canDEInsertComponentInstance: DesignerAbilityDefinition =
  canDEInsertComponentInstanceDefinition;

export const canDEModifySelectedCampaignComponentDefinition: DesignerAbilityDefinition =
  canDEModifySelectedCampaignComponentDefinitionDefinition;

/**
 * Designer Extension ability to know if the user can update text or link values.
 * This is useful to ensure in Edit mode or secondary locales that certain content is editable.
 */
export const canDEEdit: DesignerAbilityDefinition = canDEEditDefinition;

/**
 * Designer Extension ability for updating component instance props.
 * Preserves the existing Design-mode access envelope. Build-mode writes also
 * require current page or branch content access. Both paths exclude secondary
 * locales until this API can use localization-aware storage.
 */
export const canDESetInstanceProps: DesignerAbilityDefinition =
  canDESetInstancePropsDefinition;

export const canDEModifyImageElements: DesignerAbilityDefinition =
  canDEModifyImageElementsDefinition;

export const canDEWriteCodeSettings: DesignerAbilityDefinition =
  canDEWriteCodeSettingsDefinition;

/**
 * This ability is almost identical to `canCreateSymbols` but is used for Designer Extensions.
 * The difference here is that we do not want to allow unauthorized users to create components.
 * This should only be used for Designer Extension APIs.
 */
export const canDECreateComponents: DesignerAbilityDefinition =
  canDECreateComponentsDefinition;

/**
 * This ability is almost identical to `canModifyComponentsOnCanvas` but is used for Designer Extensions.
 * The difference here is that we do not want to allow unauthorized users to modify components.
 * This should only be used for Designer Extension APIs.
 */
export const canDEModifyComponents: DesignerAbilityDefinition =
  canDEModifyComponentsDefinition;

/**
 * This ability ensures a user can create style blocks in a Designer Extension.
 * This should only be used for Designer Extension APIs.
 */
export const canDECreateStyleBlocks: DesignerAbilityDefinition =
  canDECreateStyleBlocksDefinition;

/**
 * This ability ensures a user can modify a style block in a Designer Extension.
 * This should only be used for Designer Extension APIs.
 */
export const canDEModifyStyleBlocks: DesignerAbilityDefinition =
  canDEModifyStyleBlocksDefinition;

// Designer Extension API ability for accessing assets.
export const canDEAccessAssets: DesignerAbilityDefinition =
  canDEAccessAssetsDefinition;

// Designer Extension API ability for managing/creating assets.
export const canDEManageAssets: DesignerAbilityDefinition =
  canDEManageAssetsDefinition;

// Designer Extension API ability for creating pages.
export const canDECreatePage: DesignerAbilityDefinition =
  canDECreatePageDefinition;

// Designer Extension API ability for reading page settings.
export const canDEReadPageSettings: DesignerAbilityDefinition =
  canDEReadPageSettingsDefinition;

// Designer Extension API ability for managing page settings.
export const canDEManagePageSettings: DesignerAbilityDefinition =
  canDEManagePageSettingsDefinition;

export const canDEReadInteractions: DesignerAbilityDefinition =
  canDEReadInteractionsDefinition;

export const canDEReadVariables: DesignerAbilityDefinition =
  canDEReadVariablesDefinition;

export const canDEModifyInteractions: DesignerAbilityDefinition =
  canDEModifyInteractionsDefinition;

export const canDEModifyVariables: DesignerAbilityDefinition =
  canDEModifyVariablesDefinition;

/**
 * Defines if a user can see the marketplace apps callout
 */
export const canSeeAppsCallout = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    workflows: {canvas: true},
  },
] as const);

/** Whether in-product callouts (`CalloutPopoverBridge`) can show. Hidden on campaign sites. */
export const canShowInProductCallouts = defineAbilityConfig({
  siteKinds: {
    webflow: true,
    tutorial: true,
    cloudapp: true,
    optimize: true,
    campaign: false,
  },
} as const);

/**
 * This ability is used to determine whether or not the user can access the Import from Link feature.
 */
export const canAccessImportFromLink = defineAbilityConfig({
  abilities: {canDesign: true},
  featureFlags: {[FEATURE_FLAGS.IMPORT_FROM_LINK]: true},
} as const);

// Whether the user can access the Data mode in the top bar
// multimodal navigation.
export const canAccessDataMode = defineAbilityConfig([
  {abilities: {canAccessEcomm: true}},
  {abilities: {canAccessCMS: true}},
  {abilities: {canAccessUserSystems: true}},
] as const);
